import { useQuery, useMutation } from 'react-query'
import useAxiosWithAuth from 'api/useAxiosWithAuth'
import { LabType } from 'context/LabSchedulingContext/LabSchedulingProvider'
import { LabAppointment } from 'types/appointments'
import { zonedTimeToUtc, utcToZonedTime, format } from 'date-fns-tz'

interface LabService {
  id: number
  name: string
  description?: string
}

export interface LabAddress {
  address1: string
  address2: string
  city: string
  state: string
  zipcode: string
  latitude: string
  longitude: string
  timezone: string
}

interface TimeSlot {
  date: string
  time: string
}

export interface LabLocation {
  id: string
  name: string
  address: LabAddress
  slots: TimeSlot[]
  distance: number
}

export interface NearbyTimeSlotsResponse {
  origin: {
    latitude: number
    longitude: number
  }
  locations: LabLocation[]
}

export type UseNearbyTimeSlotsDto = {
  selectedLab: LabType
  address: string
  radius: number
  startDate: string
  numberOfDays: number
}

export type UseAvailableDaysDto = UseNearbyTimeSlotsDto

type UseScheduleAppointmentDto = {
  selectedLab: LabType
  locationId: string
  appointmentTime: string
  selectedAddress: string
}

type useModifyAppointmentDto = UseScheduleAppointmentDto & {
  confirmationNumber: string
}

type useCancelAppointmentDto = {
  selectedLab: LabType
  confirmationNumber: string
}

const LAB_SCHEDULING = 'lab-scheduling'
const LAB_SERVICES_QUERY_KEY = `${LAB_SCHEDULING}-lab-services`
const LAB_APPOINTMENTS_QUERY_KEY = `${LAB_SCHEDULING}-appointments`
const LAB_CACHED_TIMESLOTS_QUERY_KEY = `${LAB_SCHEDULING}-cached-timeslots`

export const useLabScheduling = () => {
  const axiosWithAuth = useAxiosWithAuth()

  const useAppointments = () => {
    return useQuery(
      LAB_APPOINTMENTS_QUERY_KEY,
      async () => {
        try {
          const response = await axiosWithAuth.get(
            '/api/v1/lab-scheduling/appointments'
          )
          return response.data as LabAppointment[]
        } catch (error) {
          throw error
        }
      },
      {
        retry: 1,
      }
    )
  }

  const {
    data: labAppointments,
    isLoading: isLabAppointmentsLoading,
    refetch: refetchLabAppointments,
  } = useAppointments()

  const useServices = (selectedLab: LabType) => {
    return useQuery(
      [LAB_SERVICES_QUERY_KEY, selectedLab],
      async () => {
        if (!selectedLab) {
          throw new Error('No lab type selected')
        }

        try {
          const response = await axiosWithAuth.get(
            '/api/v1/lab-scheduling/services',
            {
              params: { selectedLab },
            }
          )
          return response.data as LabService[]
        } catch (error) {
          throw error
        }
      },
      {
        enabled: !!selectedLab,
        retry: 1,
      }
    )
  }

  const useAvailableDays = () => {
    return useMutation(async (body: UseAvailableDaysDto) => {
      const { selectedLab, ...requestBody } = body

      if (!selectedLab) {
        throw new Error('No lab type selected')
      }

      try {
        const response = await axiosWithAuth.post(
          '/api/v1/lab-scheduling/available-days',
          requestBody,
          {
            params: { selectedLab },
          }
        )
        return response.data as string[]
      } catch (error) {
        throw error
      }
    })
  }

  const useAvailableDaysQuery = (
    selectedLab: LabType,
    address: string,
    radius: number,
    startDate: string,
    numberOfDays: number
  ) => {
    return useQuery(
      ['available-days', selectedLab, address, radius, startDate, numberOfDays],
      async () => {
        if (!selectedLab || !address || !startDate) {
          throw new Error('Missing required parameters')
        }

        try {
          const response = await axiosWithAuth.post(
            '/api/v1/lab-scheduling/available-days',
            {
              address,
              radius,
              startDate,
              numberOfDays,
            },
            {
              params: { selectedLab },
            }
          )
          return response.data as string[]
        } catch (error) {
          throw error
        }
      },
      {
        enabled: !!selectedLab && !!address && !!startDate,
        retry: 1,
        retryDelay: 3000,
        staleTime: 5 * 60 * 1000, // 5 minutes
        cacheTime: 30 * 60 * 1000, // 30 minutes
      }
    )
  }

  const useNearbyTimeSlots = () => {
    return useMutation(async (body: UseNearbyTimeSlotsDto) => {
      const { selectedLab, ...requestBody } = body

      if (!selectedLab) {
        throw new Error('No lab type selected')
      }

      try {
        const response = await axiosWithAuth.post(
          '/api/v1/lab-scheduling/nearby-time-slots',
          requestBody,
          {
            params: { selectedLab },
          }
        )
        return response.data as NearbyTimeSlotsResponse
      } catch (error) {
        throw error
      }
    })
  }

  const useCachedTimeSlots = (
    selectedLab: LabType,
    address: string,
    radius: number,
    startDate: string,
    daysToCache: number
  ) => {
    return useQuery(
      [LAB_CACHED_TIMESLOTS_QUERY_KEY, selectedLab, address, radius, startDate],
      async () => {
        if (!selectedLab || !address || !startDate) {
          throw new Error('Missing required parameters')
        }

        try {
          const response = await axiosWithAuth.post(
            '/api/v1/lab-scheduling/nearby-time-slots',
            {
              address,
              radius,
              startDate,
              numberOfDays: daysToCache,
            },
            {
              params: { selectedLab },
            }
          )
          return response.data as NearbyTimeSlotsResponse
        } catch (error) {
          throw error
        }
      },
      {
        enabled: !!selectedLab && !!address && !!startDate,
        retry: 1,
        retryDelay: 3000,
        staleTime: 5 * 60 * 1000, // 5 minutes
        cacheTime: 30 * 60 * 1000, // 30 minutes
      }
    )
  }

  // Utility function to filter cached data by specific date
  const filterTimeSlotsByDate = (
    cachedResponse: NearbyTimeSlotsResponse | undefined,
    targetDate: string,
    displayTimezone?: string
  ): NearbyTimeSlotsResponse | null => {
    if (!cachedResponse) {
      return null
    }

    const filteredLocations = cachedResponse.locations
      .map((location) => {
        const filteredSlots = location.slots.filter((slot) => {
          // First filter by the original date
          if (slot.date !== targetDate) {
            return false
          }

          // If a display timezone is provided, check if the slot still falls on the target date
          // after timezone conversion
          if (displayTimezone && location.address.timezone) {
            const slotDateTime = `${slot.date}T${slot.time}:00`

            // Convert from lab timezone to UTC, then to display timezone
            const utcDate = zonedTimeToUtc(
              slotDateTime,
              location.address.timezone
            )
            const displayDate = utcToZonedTime(utcDate, displayTimezone)

            // Check if the converted time still falls on the target date
            const convertedDateStr = format(displayDate, 'yyyy-MM-dd', {
              timeZone: displayTimezone,
            })

            return convertedDateStr === targetDate
          }

          return true
        })

        return {
          ...location,
          slots: filteredSlots,
        }
      })
      .filter((location) => location.slots.length > 0)

    return {
      ...cachedResponse,
      locations: filteredLocations,
    }
  }

  // Utility function to get available days from cached data
  const getAvailableDaysFromCache = (
    cachedResponse: NearbyTimeSlotsResponse | undefined
  ): string[] => {
    if (!cachedResponse) {
      return []
    }

    const allDates = new Set<string>()
    cachedResponse.locations.forEach((location) => {
      location.slots.forEach((slot) => {
        allDates.add(slot.date)
      })
    })

    return Array.from(allDates).sort()
  }

  const useScheduleAppointment = () => {
    return useMutation(async (body: UseScheduleAppointmentDto) => {
      const { selectedLab, ...requestBody } = body

      if (!selectedLab) {
        throw new Error('No lab type selected')
      }

      try {
        const response = await axiosWithAuth.post(
          '/api/v1/lab-scheduling/schedule-appointment',
          requestBody,
          {
            params: { selectedLab },
          }
        )
        return response.data
      } catch (error) {
        throw error
      }
    })
  }

  const useModifyAppointment = () => {
    return useMutation(async (body: useModifyAppointmentDto) => {
      const { selectedLab, ...requestBody } = body

      if (!selectedLab) {
        throw new Error('No lab type selected')
      }

      try {
        const response = await axiosWithAuth.post(
          '/api/v1/lab-scheduling/modify-appointment',
          requestBody,
          {
            params: { selectedLab },
          }
        )
        return response.data
      } catch (error) {
        throw error
      }
    })
  }

  const useCancelAppointment = () => {
    return useMutation(async (body: useCancelAppointmentDto) => {
      const { selectedLab, ...requestBody } = body

      if (!selectedLab) {
        throw new Error('No lab type selected')
      }

      try {
        const response = await axiosWithAuth.post(
          '/api/v1/lab-scheduling/cancel-appointment',
          requestBody,
          {
            params: { selectedLab },
          }
        )
        return response.data
      } catch (error) {
        throw error
      }
    })
  }

  const useHandleExternalSchedule = () => {
    return useMutation(async () => {
      const response = await axiosWithAuth.post(
        '/api/v1/lab-scheduling/external-schedule'
      )
      return response.data
    })
  }

  return {
    useAppointments,
    useServices,
    useAvailableDays,
    useAvailableDaysQuery,
    useNearbyTimeSlots,
    useCachedTimeSlots,
    filterTimeSlotsByDate,
    getAvailableDaysFromCache,
    useScheduleAppointment,
    useModifyAppointment,
    useCancelAppointment,
    labAppointments,
    isLabAppointmentsLoading,
    refetchLabAppointments,
    useHandleExternalSchedule,
  }
}
